import React from 'react';
import WbLoading from '@sonifi/wb-widget/components/WbLoading/WbLoading';
import { Outlet, useLocation, useNavigation } from 'react-router';
import { ROUTES_PATH } from './routesConstants';
import TreeAndContentSkeleton from '../components/Skeletons/TreeAndContentSkeleton/TreeAndContentSkeleton';
import TableSkeleton from '../components/Skeletons/TableSkeleton/TableSkeleton';

// Helper to match a pattern with params (e.g., /products/:groupId/guest_request/menu/:platform/:id?)
export function matchPathPattern(pattern, path) {
  // Remove all trailing slashes and normalize - also handle leading slashes
  const cleanPattern = pattern.replace(/^\/+|\/+$/g, '').trim();
  const cleanPath = path.replace(/^\/+|\/+$/g, '').trim();

  // Replace :param with wildcard, handling optional parameters
  let regexPattern = cleanPattern
    .replace(/:\w+\?/g, '(?:[^/]+)?') // Handle optional parameters like :id?
    .replace(/:\w+/g, '[^/]+'); // Handle required parameters like :groupId

  // Handle optional literal path segments (React Router `segment?` syntax, e.g., /slides?)
  regexPattern = regexPattern.replace(/\/(\w+)\?/g, '(?:/$1)?');

  // Handle trailing optional segments by making them truly optional
  regexPattern = regexPattern.replace(/\/\(\?:\[\^\/\]\+\)\?$/, '(?:/[^/]+)?');

  const regex = new RegExp(`^${regexPattern}$`);

  return regex.test(cleanPath);
}

/**
 * Maps routes to their loading skeleton components and loading behavior.
 * Each route can specify whether to show loading state when navigating to the same path.
 */
const destinationsPaths = {
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/happenings/slides?/:slideId?`]: {
    component: TreeAndContentSkeleton,
    ignoreSamePath: true,
  },
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/hotel_directory/:platform/:id?`]: {
    component: TreeAndContentSkeleton,
    ignoreSamePath: false,
  },
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/guest_request/menu/:platform/:id?`]: {
    component: TreeAndContentSkeleton,
    ignoreSamePath: false,
  },
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/guest_request/orders`]: {
    component: () => <TableSkeleton rowCount={25} columnCount={5} />,
    ignoreSamePath: true,
  },
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/menu/:platform/:id?`]:
    {
      component: TreeAndContentSkeleton,
      ignoreSamePath: false,
    },
  [`${ROUTES_PATH.PRODUCTS.PATH}/:groupId/in_room_dining/:revenueCenterId/orders/:orderId?`]:
    {
      component: () => <TableSkeleton rowCount={25} />,
      ignoreSamePath: true,
    },
  [`${ROUTES_PATH.ADMIN.JOBS}/:id?`]: {
    component: () => <TableSkeleton rowCount={25} columnCount={7} />,
    ignoreSamePath: true,
  },
  [`${ROUTES_PATH.ADMIN.PROPERTIES}/:id?`]: {
    component: () => <TableSkeleton rowCount={25} columnCount={4} />,
    ignoreSamePath: true,
  },
  [`${ROUTES_PATH.ADMIN.USERS}/:id?`]: {
    component: () => <TableSkeleton rowCount={25} columnCount={7} />,
    ignoreSamePath: true,
  },
};

/**
 * Displays a loading skeleton based on the destination route.
 * Falls back to a simple loading message if no skeleton is configured.
 */
const SkeletonPage = ({ destinationPath }) => {
  const pathConfig = destinationsPaths[destinationPath];

  const SkeletonComponent = pathConfig?.component;

  if (SkeletonComponent) {
    return <SkeletonComponent />;
  }
  return <WbLoading />;
};

/**
 * Manages loading states during route navigation.
 * Shows skeleton loading screens while routes are loading.
 * Can be configured to skip loading states when navigating to the same path.
 */
const RouteLoadingManager = () => {
  const navigation = useNavigation();
  const location = useLocation();

  // Try to get destination path from navigation, fallback to current location
  const destinationPath = navigation.location?.pathname || '';

  // Find the matching route pattern for the destination path
  const matchingPattern = Object.keys(destinationsPaths).find((pattern) =>
    matchPathPattern(pattern, destinationPath),
  );

  // Find the matching pattern for the current location
  const currentPattern = Object.keys(destinationsPaths).find((pattern) =>
    matchPathPattern(pattern, location.pathname),
  );

  // Ignore loading if both current and destination match the same pattern
  const ignoreSamePath =
    destinationsPaths[matchingPattern]?.ignoreSamePath &&
    matchingPattern === currentPattern;

  // Simplified version that only handles navigation states
  if (navigation.state === 'loading' && !ignoreSamePath) {
    // Use navigation.location to get the destination path during navigation
    return <SkeletonPage destinationPath={matchingPattern} />;
  }
  return <Outlet />;
};

export default RouteLoadingManager;
